'use client'

import React, { useRef, useEffect } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ITheme } from '@/lib/interfaces/interfaces'
import '@/sass/menu/CartaSelector.scss'

export interface CartaInfo {
  id: string
  name: string
}

interface Props {
  cartas: CartaInfo[]
  activeCartaId: string
  variant: 'classic' | 'modern'
  preserveDelivery?: boolean
  theme?: ITheme
}

const CartaSelector = ({ cartas, activeCartaId, variant, preserveDelivery, theme }: Props) => {
  const pathname = usePathname()
  // Sentinel div (non-sticky) placed just before the nav. We scroll to this instead
  // of the nav itself, because sticky elements at top:0 are considered "in view"
  // by the browser even when the user is scrolled far below them — scrollIntoView
  // and window.scrollTo(offsetTop) both become no-ops in that case. A regular div
  // in the normal flow is always correctly handled by scrollIntoView.
  const sentinelRef = useRef<HTMLDivElement>(null)
  const prevCartaId = useRef(activeCartaId)

  useEffect(() => {
    if (prevCartaId.current !== activeCartaId) {
      prevCartaId.current = activeCartaId
      sentinelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [activeCartaId])

  if (cartas.length <= 1) return null

  const buildHref = (cartaId: string) => {
    // Same pathname as the current URL (keeps the language segment, or the
    // canonical /menu/{slug} when none is present) and only swaps the ?carta=
    // query param. Changing only the query string prevents the App Router from
    // remounting the whole page subtree on the first carta switch.
    const params = new URLSearchParams()
    if (cartaId) params.set('carta', cartaId)
    if (preserveDelivery) params.set('order', 'delivery')
    const qs = params.toString()
    return qs ? `${pathname}?${qs}` : pathname
  }

  const themeVars = theme
    ? variant === 'classic'
      ? {
        '--carta-accent': theme.footer.backgroundColor,
        '--carta-accent-text': theme.footer.color,
        '--carta-bg': theme.sectionBgColor,
        '--carta-text': theme.items.nameColor,
        '--carta-border': theme.items.dividerColor,
        ...(theme.navBarBgColor ? { '--carta-nav-bg': theme.navBarBgColor } : {})
      } as React.CSSProperties
      : {
        '--carta-fg': theme.footer.color,
        '--carta-bg': theme.footer.backgroundColor
      } as React.CSSProperties
    : {}

  return (
    <>
      {/* Non-sticky scroll target — sits at the nav's natural document position */}
      <div ref={sentinelRef} aria-hidden='true' />
      <nav className={`carta-selector ${variant}-variant`} aria-label='Cartas' style={themeVars}>
        <div className='carta-tabs-track'>
          {cartas.map((carta) => (
            <Link
              key={carta.id}
              href={buildHref(carta.id)}
              className={`carta-tab${carta.id === activeCartaId ? ' active' : ''}`}
              prefetch={false}
              scroll={false}
            >
              {carta.name}
            </Link>
          ))}
        </div>
      </nav>
    </>
  )
}

export default CartaSelector
